import { associationIdName, fetchGet, siteNumber, vvSettings } from './common';
import { stripHtml } from '../lib/stripHtml';

// ── Types ─────────────────────────────────────────────────────────────────────

export type BillPosition = 'support' | 'oppose' | 'neutral';
export type BillPositionFilter = 'all' | BillPosition;

export interface BillGovernment {
    /** Relative government URI, e.g. "./USA" (federal) or "./USA/TX". */
    uri: string;
    name: string;
}

export interface BillSpotlight {
    sessionDisplayName: string;
    name: string;
    /** Association-authored HTML (CKEditor). */
    explanation: string | null;
    categoryName: string | null;
    categorySortOrder: number | null;
    position: BillPosition;
    governmentUrl: string;
    /** Relative bill URL, e.g. "./USA/TX/LegislativeProposals/20250114r/HB 1". Doubles as the bill's id. */
    legislativeProposalUrl: string;
    governmentAbbreviation: string;
}

export interface BillSession {
    /** Normalized session name, e.g. "20250114r"; sent as the `session` search parameter. */
    name: string;
    displayName: string;
}

export interface BillSearchItem {
    display: string;
    url: string;
}

export interface BillSearchPage {
    proposals: BillSearchItem[];
    /** Display names of the sessions the results came from (or that were searched, when none matched). */
    sessionNames: string[];
    continuationToken: string | null;
    totalResults: number;
}

export interface BillSponsor {
    /** Formatted description, e.g. "Rep. Jerry Nadler (D-NY-12)". */
    description: string;
    name: string;
    subtitle: string;
    initials: string;
    /** 0 when the sponsor has no directory profile. */
    officeOccupancyId: number;
    photoUrl: string | null;
}

export interface BillAction {
    /** Display date as formatted by the server in the association's culture. */
    date: string;
    description: string;
}

export interface BillRelatedProposal {
    identifierDisplay: string;
    shortTitle: string;
    sessionDisplayName: string;
    url: string;
}

export interface BillDetail {
    url: string;
    identifier: string;
    identifierDisplay: string;
    governmentAbbreviation: string;
    sessionDisplayName: string;
    /** Spotlight name when the association tracks the bill, otherwise "H.R. 1234: Short title". */
    title: string;
    shortTitle: string;
    fullTitle: string;
    primarySponsors: BillSponsor[];
    otherSponsors: BillSponsor[];
    actions: BillAction[];
    committees: string[];
    urls: string[];
    relatedProposals: BillRelatedProposal[];
    /** Present when the association tracks this bill as key legislation. */
    spotlight: BillSpotlight | null;
}

/** `window.vvSettings.bills`, injected by Areas/Advocacy/Views/Bills/Svelte.cshtml. */
export interface BillsSettings {
    defaultGovernmentName?: string | null;
    initialSearch?: string | null;
    spotlightsCsvUrl?: string | null;
    /**
     * Local-development mocks (examples/Bills/billsSettings.js, loaded by mock.html). When present
     * every API function short-circuits to these raw payloads; otherwise the real fetch path runs.
     */
    mock?: BillsMockData;
}

export interface BillsMockData {
    spotlights?: RawSpotlight[];
    governments?: BillGovernment[];
    sessions?: Record<string, BillSession[]>;
    proposals?: BillSearchItem[];
    details?: Record<string, RawDetail>;
    /** Officials the mock Profiles endpoint knows; when omitted every sponsor is treated as having a profile. */
    profileIds?: number[];
}

// ── Raw REST contracts (camelCase, see RestApi BillsLegacyParityTest) ─────────

interface RawSpotlight {
    sessionDisplayName: string | null;
    name: string | null;
    explanation: string | null;
    categoryName: string | null;
    categorySortOrder: number | null;
    position: number | null;
    governmentUrl: string | null;
    legislativeProposalUrl: string | null;
    governmentAbbreviation: string | null;
}

interface RawSearchResponse {
    proposals: BillSearchItem[] | null;
    sessions: { displayName: string }[] | null;
    continuationToken: string | null;
    totalPages: number;
    totalResults: number;
}

interface RawSponsor {
    description: string | null;
    officeOccupancyId: number;
    photoUrl: string | null;
}

interface RawAction {
    display: string | null;
    date?: string | null;
    description?: string | null;
}

interface RawDetail {
    identifier: string | null;
    identifierDisplay: string | null;
    governmentAbbreviation: string | null;
    sessionDisplayName: string | null;
    shortTitle: string | null;
    fullTitle: string | null;
    primarySponsors: RawSponsor[] | null;
    otherSponsors: RawSponsor[] | null;
    actions: RawAction[] | null;
    committees: string[] | null;
    urls: string[] | null;
    relatedProposals: (BillRelatedProposal & { identifier?: string })[] | null;
}

// ── Settings ──────────────────────────────────────────────────────────────────

export const billsSettings: BillsSettings = vvSettings?.bills ?? {};

const mock = billsSettings.mock;

function isExampleMode(): boolean {
    return !!mock;
}

// ── URL helpers ───────────────────────────────────────────────────────────────

/** "./USA/TX/LegislativeProposals/x/HB 1" → "USA/TX/LegislativeProposals/x/HB%201" */
function toApiPath(relativeUri: string): string {
    return relativeUri
        .replace(/^\.\//, '')
        .split('/')
        .map(encodeURIComponent)
        .join('/');
}

function sameGovernmentUri(a: string, b: string): boolean {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Spotlight URLs can carry the display identifier ("…/H.R. 1") where search results carry the
 * prefix ("…/HR 1"); the server accepts both, so compare with dots and spaces removed from the
 * bill segment (legacy BillTrackerApplication does the same).
 */
function sameBillUrl(a: string, b: string): boolean {
    const key = (url: string) => {
        const parts = url.trim().toLowerCase().split('/');
        parts[parts.length - 1] = parts[parts.length - 1].replace(/[.\s]/g, '');
        return parts.join('/');
    };
    return key(a) === key(b);
}

/** SPA route for an official's profile, shown inside the Bills page (no address needed, unlike the directory). */
export function officialProfileRoute(officeOccupancyId: number): string {
    return `/Official/${officeOccupancyId}`;
}

/** Messaging still happens in the directory app, which matches the user's address to the official. */
export function buildOfficialMessageUrl(officeOccupancyId: number): string {
    const siteSegment = siteNumber ? `/${siteNumber}` : '';
    return `/${associationIdName}${siteSegment}/Directory#/Officials/${officeOccupancyId}/Message`;
}

/** SPA route for the full-page detail view of any bill. */
export function billDetailRoute(legislativeProposalUrl: string): string {
    return `/Bill/${encodeURIComponent(legislativeProposalUrl)}`;
}

/**
 * Legacy deep links (BillsController.BillRedirect / BillIdRedirect, AdminSite emails) land on
 * `#/?page=legislation-"./USA/TX/LegislativeProposals/..."`. Returns the bill URL, or null.
 */
export function parseLegacyBillHash(querystring: string | undefined): string | null {
    const page = new URLSearchParams(querystring ?? '').get('page');
    const match = page?.match(/^legislation-"?(.+?)"?$/);
    return match ? match[1] : null;
}

// ── Normalization ─────────────────────────────────────────────────────────────

/** Legacy position encoding: 2 = support, 1 = oppose, anything else = neutral. */
export function normalizeBillPosition(raw: number | null | undefined): BillPosition {
    if (raw === 2) return 'support';
    if (raw === 1) return 'oppose';
    return 'neutral';
}

function normalizeSpotlight(raw: RawSpotlight): BillSpotlight {
    return {
        sessionDisplayName: raw.sessionDisplayName ?? '',
        name: raw.name ?? '',
        explanation: raw.explanation || null,
        categoryName: raw.categoryName || null,
        categorySortOrder: raw.categorySortOrder ?? null,
        position: normalizeBillPosition(raw.position),
        governmentUrl: raw.governmentUrl ?? '',
        legislativeProposalUrl: raw.legislativeProposalUrl ?? '',
        governmentAbbreviation: raw.governmentAbbreviation ?? '',
    };
}

function normalizeSponsor(raw: RawSponsor): BillSponsor {
    const description = raw.description ?? '';
    const parenIdx = description.lastIndexOf('(');
    const name = parenIdx > 0 ? description.slice(0, parenIdx).trim() : description;
    const subtitle = parenIdx > 0 ? description.slice(parenIdx + 1).replace(/\)\s*$/, '').trim() : '';
    const initials = name
        .replace(/^(Rep\.|Sen\.|Del\.|Rep|Sen|Del|Assemblymember|Commissioner)\s+/i, '')
        .split(/\s+/)
        .filter((w) => /^[A-Za-z]/.test(w))
        .map((w) => w[0].toUpperCase())
        .filter((_, i, all) => i === 0 || i === all.length - 1)
        .join('');

    return {
        description,
        name,
        subtitle,
        initials,
        officeOccupancyId: raw.officeOccupancyId ?? 0,
        photoUrl: raw.photoUrl || null,
    };
}

/**
 * `display` is "{culture-formatted date} - {text}". With `description` the date is whatever
 * precedes it, so action text containing " - " survives; hosts without it fall back to the first " - ".
 */
function normalizeAction(raw: RawAction): BillAction {
    const display = raw.display ?? '';
    if (raw.description != null) {
        const suffix = ` - ${raw.description}`;
        const date = display.endsWith(suffix) ? display.slice(0, display.length - suffix.length) : raw.date ?? '';
        return { date, description: raw.description };
    }
    const sep = display.indexOf(' - ');
    return sep > 0
        ? { date: display.slice(0, sep), description: display.slice(sep + 3) }
        : { date: '', description: display };
}

function normalizeDetail(raw: RawDetail, url: string, spotlight: BillSpotlight | null): BillDetail {
    const identifierDisplay = raw.identifierDisplay ?? raw.identifier ?? '';
    const shortTitle = raw.shortTitle ?? '';
    return {
        url,
        identifier: raw.identifier ?? '',
        identifierDisplay,
        governmentAbbreviation: raw.governmentAbbreviation ?? '',
        sessionDisplayName: raw.sessionDisplayName ?? '',
        title: spotlight?.name || (shortTitle ? `${identifierDisplay}: ${shortTitle}` : identifierDisplay),
        shortTitle,
        fullTitle: stripHtml(raw.fullTitle),
        primarySponsors: (raw.primarySponsors ?? []).map(normalizeSponsor),
        otherSponsors: (raw.otherSponsors ?? []).map(normalizeSponsor),
        actions: (raw.actions ?? []).map(normalizeAction),
        committees: raw.committees ?? [],
        urls: raw.urls ?? [],
        relatedProposals: (raw.relatedProposals ?? []).map((r) => ({
            identifierDisplay: r.identifierDisplay ?? r.identifier ?? '',
            shortTitle: r.shortTitle ?? '',
            sessionDisplayName: r.sessionDisplayName ?? '',
            url: r.url ?? '',
        })),
        spotlight,
    };
}

// ── Endpoints ─────────────────────────────────────────────────────────────────

/** Jurisdictions relevant to the association that have legislation (REST `Bills/governments`). */
export async function getBillGovernments(): Promise<BillGovernment[]> {
    if (isExampleMode()) return mock?.governments ?? [];
    const response = await fetchGet<BillGovernment[]>(
        `bills/governments?association=${encodeURIComponent(associationIdName)}`,
        true
    );
    return response ?? [];
}

/** The association's key legislation, in the platform's standard legislation order. */
export async function getBillSpotlights(): Promise<BillSpotlight[]> {
    const raw = isExampleMode()
        ? mock?.spotlights ?? []
        : await fetchGet<RawSpotlight[]>(
              `Associations/${encodeURIComponent(associationIdName)}/LegislativeProposalSpotlights`,
              true
          );
    return (raw ?? []).map(normalizeSpotlight);
}

/** Active legislative sessions for a jurisdiction; a search runs across all of them. */
export async function getBillSessions(governmentUri: string): Promise<BillSession[]> {
    if (isExampleMode()) return mock?.sessions?.[governmentUri] ?? [];
    const response = await fetchGet<BillSession[]>(`Governments/${toApiPath(governmentUri)}/LegislativeSessions`, true);
    return response ?? [];
}

/**
 * Search a jurisdiction's legislation by bill number or keyword. Pass the previous page's
 * `continuationToken` to fetch the next page ("Show more").
 */
export async function searchBills(
    governmentUri: string,
    sessions: BillSession[],
    search: string,
    continuationToken?: string | null
): Promise<BillSearchPage> {
    if (isExampleMode()) return searchMockProposals(governmentUri, search);

    const params = new URLSearchParams({ search });
    for (const s of sessions) params.append('session', s.name);
    if (continuationToken) params.set('continuationToken', continuationToken);

    const raw = await fetchGet<RawSearchResponse>(
        `Governments/${toApiPath(governmentUri)}/LegislativeProposals?${params.toString()}`,
        true
    );
    return {
        proposals: raw?.proposals ?? [],
        sessionNames: (raw?.sessions ?? []).map((s) => s.displayName),
        continuationToken: raw?.continuationToken || null,
        totalResults: raw?.totalResults ?? 0,
    };
}

/** Full detail for any bill (key legislation or not), merged with the association's spotlight data. */
export async function getBillDetail(legislativeProposalUrl: string, spotlights: BillSpotlight[]): Promise<BillDetail> {
    const raw = isExampleMode()
        ? mockDetail(legislativeProposalUrl)
        : await fetchGet<RawDetail>(`Governments/${toApiPath(legislativeProposalUrl)}`, true);
    return normalizeDetail(raw, legislativeProposalUrl, findSpotlight(spotlights, legislativeProposalUrl));
}

/** GET query strings are capped at 2048 characters by IIS; ~40 identifiers stays well under it. */
const PROFILE_LOOKUP_BATCH_SIZE = 40;

/**
 * Which sponsors have a directory profile. Sponsors of older bills often left office, and the
 * Profiles endpoint returns null for them, so their names should not link to an empty page.
 */
export async function getOfficialsWithProfiles(officeOccupancyIds: number[]): Promise<Set<number>> {
    const ids = [...new Set(officeOccupancyIds.filter((id) => id > 0))];
    if (isExampleMode()) {
        const known = mock?.profileIds;
        return new Set(known ? ids.filter((id) => known.includes(id)) : ids);
    }

    const batches: number[][] = [];
    for (let i = 0; i < ids.length; i += PROFILE_LOOKUP_BATCH_SIZE) {
        batches.push(ids.slice(i, i + PROFILE_LOOKUP_BATCH_SIZE));
    }
    const responses = await Promise.all(
        batches.map((batch) => {
            const filter = JSON.stringify({ identifiers: batch.map((id) => ({ id, type: 'E' })) });
            return fetchGet<({ id: string } | null)[]>(
                `Profiles?filter=${encodeURIComponent(filter)}&association=${encodeURIComponent(associationIdName)}`,
                true
            );
        })
    );
    return new Set(
        responses
            .flatMap((response) => response ?? [])
            .filter((profile): profile is { id: string } => !!profile)
            .map((profile) => Number(profile.id))
    );
}

export function findSpotlight(spotlights: BillSpotlight[], legislativeProposalUrl: string): BillSpotlight | null {
    return spotlights.find((s) => sameBillUrl(s.legislativeProposalUrl, legislativeProposalUrl)) ?? null;
}

// ── Example-mode mocks ────────────────────────────────────────────────────────

function searchMockProposals(governmentUri: string, search: string): BillSearchPage {
    const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
    const proposals = (mock?.proposals ?? []).filter(
        (p) => p.url.startsWith(`${governmentUri}/LegislativeProposals/`) && terms.every((term) => p.display.toLowerCase().includes(term))
    );
    return {
        proposals,
        sessionNames: (mock?.sessions?.[governmentUri] ?? []).map((s) => s.displayName),
        continuationToken: null,
        totalResults: proposals.length,
    };
}

function mockDetail(url: string): RawDetail {
    const found = Object.entries(mock?.details ?? {}).find(([key]) => sameBillUrl(key, url));
    if (!found) throw new Error(`[Bills] Example mode: no mock detail for ${url}`);
    return found[1];
}

// ── Key legislation list helpers ──────────────────────────────────────────────

/**
 * Legacy BillTrackerApplication rule: when the association's jurisdictions do not start with
 * Federal, the first one wins; otherwise the server-configured default (by display name).
 */
export function defaultGovernmentUri(governments: BillGovernment[], defaultGovernmentName?: string | null): string {
    if (governments.length === 0) return '';
    if (governments[0].name !== 'Federal') return governments[0].uri;
    return (governments.find((g) => g.name === defaultGovernmentName) ?? governments[0]).uri;
}

/** Distinct category names, highest sortOrder first (legacy order). */
export function getCategoryNames(spotlights: BillSpotlight[]): string[] {
    const byName = new Map<string, number>();
    for (const s of spotlights) {
        if (!s.categoryName) continue;
        const order = s.categorySortOrder ?? 0;
        const existing = byName.get(s.categoryName);
        if (existing === undefined || order > existing) byName.set(s.categoryName, order);
    }
    return [...byName.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name);
}

/** Jurisdictions with at least one key bill, in the order the API returned them. */
export function getKeyLegislationGovernments(governments: BillGovernment[], spotlights: BillSpotlight[]): BillGovernment[] {
    return governments.filter((g) => spotlights.some((s) => sameGovernmentUri(s.governmentUrl, g.uri)));
}

export function filterSpotlights(
    spotlights: BillSpotlight[],
    governmentUri: string,
    categoryName: string,
    position: BillPositionFilter
): BillSpotlight[] {
    return spotlights.filter(
        (s) =>
            sameGovernmentUri(s.governmentUrl, governmentUri) &&
            (!categoryName || s.categoryName === categoryName) &&
            (position === 'all' || s.position === position)
    );
}

/** Groups by category in sort order, uncategorized last (legacy parity). */
export function groupSpotlightsByCategory(
    spotlights: BillSpotlight[],
    categoryNames: string[]
): Array<{ category: string | null; items: BillSpotlight[] }> {
    if (categoryNames.length === 0) return [{ category: null, items: spotlights }];

    const groups: Array<{ category: string | null; items: BillSpotlight[] }> = [];
    for (const name of categoryNames) {
        const items = spotlights.filter((s) => s.categoryName === name);
        if (items.length > 0) groups.push({ category: name, items });
    }
    const uncategorized = spotlights.filter((s) => !s.categoryName);
    if (uncategorized.length > 0) groups.push({ category: null, items: uncategorized });
    return groups;
}

export function parseSessionDisplayName(sessionDisplayName: string): { congressLabel: string; yearRange: string } {
    const match = sessionDisplayName.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
    return match
        ? { congressLabel: match[1].trim(), yearRange: match[2].trim() }
        : { congressLabel: sessionDisplayName, yearRange: '' };
}

/** "H.R. 1234: Clean Energy Act" → { billId: "H.R. 1234", billTitle: "Clean Energy Act" } */
export function parseBillName(name: string): { billId: string; billTitle: string } {
    const colonIndex = name.indexOf(':');
    return colonIndex > 0
        ? { billId: name.slice(0, colonIndex).trim(), billTitle: name.slice(colonIndex + 1).trim() }
        : { billId: name, billTitle: name };
}
